import React, { useState } from 'react';
import { Player, AppConfiguration } from '../types';

interface AdminProps {
  players: Player[];
  config: AppConfiguration;
  onUpdatePlayers: (players: Player[]) => void;
  onUpdateConfig: (config: AppConfiguration) => void;
}

export const Admin: React.FC<AdminProps> = ({ players, config, onUpdatePlayers, onUpdateConfig }) => {
  const [editingPlayer, setEditingPlayer] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [newPlayerName, setNewPlayerName] = useState('');

  // Configuration editing states
  const [editingConfig, setEditingConfig] = useState<string | null>(null);
  const [tempTournamentFee, setTempTournamentFee] = useState(config.tournamentFee.toString());
  const [tempTransportTotal, setTempTransportTotal] = useState(config.transportTotal.toString());
  const [tempTeamName, setTempTeamName] = useState(config.teamName);

  const startEditing = (player: Player) => {
    setEditingPlayer(player.id);
    setEditName(player.name);
  };

  const saveEdit = () => {
    if (editingPlayer && editName.trim()) {
      const updatedPlayers = players.map(player =>
        player.id === editingPlayer
          ? { ...player, name: editName.trim() }
          : player
      );
      onUpdatePlayers(updatedPlayers);
      setEditingPlayer(null);
      setEditName('');
    }
  };

  const addPlayer = () => {
    if (newPlayerName.trim()) {
      const newPlayer: Player = {
        id: Date.now().toString(),
        name: newPlayerName.trim(),
        isActive: true
      };
      onUpdatePlayers([...players, newPlayer]);
      setNewPlayerName('');
    }
  };

  const removePlayer = (playerId: string) => {
    if (window.confirm('Are you sure you want to delete this player?')) {
      const updatedPlayers = players.filter(player => player.id !== playerId);
      onUpdatePlayers(updatedPlayers);
    }
  };

  const startConfigEdit = (field: string) => {
    setEditingConfig(field);
    if (field === 'tournamentFee') {
      setTempTournamentFee(config.tournamentFee.toString());
    } else if (field === 'transportTotal') {
      setTempTransportTotal(config.transportTotal.toString());
    } else if (field === 'teamName') {
      setTempTeamName(config.teamName);
    }
  };

  const saveConfigEdit = () => {
    if (editingConfig === 'tournamentFee') {
      const value = parseInt(tempTournamentFee);
      if (!isNaN(value) && value > 0) {
        onUpdateConfig({ ...config, tournamentFee: value });
      }
    } else if (editingConfig === 'transportTotal') {
      const value = parseInt(tempTransportTotal);
      if (!isNaN(value) && value > 0) {
        onUpdateConfig({ ...config, transportTotal: value });
      }
    } else if (editingConfig === 'teamName') {
      if (tempTeamName.trim()) {
        onUpdateConfig({ ...config, teamName: tempTeamName.trim() });
      }
    }
    setEditingConfig(null);
  };

  const activePlayerCount = players.filter(p => p.isActive).length;
  const transportPerPlayer = Math.round(config.transportTotal / activePlayerCount);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Player List */}
      <div className="bg-white border border-gray-300">
        <div className="bg-gray-500 text-white px-4 py-2 text-sm font-medium">
          Player List
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <tbody>
              {/* Create rows of 4 players each */}
              {Array.from({ length: Math.ceil(players.length / 4) }, (_, rowIndex) => (
                <tr key={rowIndex}>
                  {Array.from({ length: 4 }, (_, colIndex) => {
                    const playerIndex = rowIndex * 4 + colIndex;
                    const player = players[playerIndex];

                    if (!player) {
                      return <td key={colIndex} className="w-1/4 p-1"></td>;
                    }

                    return (
                      <td key={colIndex} className="w-1/4 p-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => removePlayer(player.id)}
                              className="text-red-500 hover:text-red-700 text-sm"
                              title="Delete player"
                            >
                              ×
                            </button>
                            {editingPlayer === player.id ? (
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onBlur={saveEdit}
                                onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                                className="text-sm border border-gray-300 rounded px-1 py-0.5 w-16"
                                autoFocus
                              />
                            ) : (
                              <span
                                onDoubleClick={() => startEditing(player)}
                                className="cursor-pointer text-sm hover:text-blue-600"
                                title="Double-click to edit"
                              >
                                {player.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Add new player row */}
              <tr>
                <td colSpan={4} className="p-1">
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={newPlayerName}
                      onChange={(e) => setNewPlayerName(e.target.value)}
                      placeholder="Add new player..."
                      className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
                      onKeyPress={(e) => e.key === 'Enter' && addPlayer()}
                    />
                    <button
                      onClick={addPlayer}
                      disabled={!newPlayerName.trim()}
                      className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Team Configuration */}
      <div className="bg-white border border-gray-300">
        <div className="bg-gray-500 text-white px-4 py-2 text-sm font-medium">
          Team Configuration
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <td className="py-1 pr-4 font-medium">Team Name:</td>
                <td className="py-1">
                  {editingConfig === 'teamName' ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={tempTeamName}
                        onChange={(e) => setTempTeamName(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs"
                        onKeyPress={(e) => e.key === 'Enter' && saveConfigEdit()}
                        autoFocus
                      />
                      <button
                        onClick={saveConfigEdit}
                        className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingConfig(null)}
                        className="px-2 py-1 bg-gray-500 text-white rounded text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span
                      onClick={() => startConfigEdit('teamName')}
                      className="cursor-pointer hover:text-blue-600"
                      title="Click to edit"
                    >
                      {config.teamName}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="py-1 pr-4 font-medium">Tournament Fee:</td>
                <td className="py-1">
                  {editingConfig === 'tournamentFee' ? (
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={tempTournamentFee}
                        onChange={(e) => setTempTournamentFee(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                        onKeyPress={(e) => e.key === 'Enter' && saveConfigEdit()}
                        autoFocus
                      />
                      <button
                        onClick={saveConfigEdit}
                        className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingConfig(null)}
                        className="px-2 py-1 bg-gray-500 text-white rounded text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span
                      onClick={() => startConfigEdit('tournamentFee')}
                      className="cursor-pointer hover:text-blue-600"
                      title="Click to edit"
                    >
                      Rs. {config.tournamentFee.toLocaleString()}/-
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="py-1 pr-4 font-medium">Transport Fee:</td>
                <td className="py-1">
                  {editingConfig === 'transportTotal' ? (
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={tempTransportTotal}
                        onChange={(e) => setTempTransportTotal(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                        onKeyPress={(e) => e.key === 'Enter' && saveConfigEdit()}
                        autoFocus
                      />
                      <button
                        onClick={saveConfigEdit}
                        className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingConfig(null)}
                        className="px-2 py-1 bg-gray-500 text-white rounded text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span
                      onClick={() => startConfigEdit('transportTotal')}
                      className="cursor-pointer hover:text-blue-600"
                      title="Click to edit"
                    >
                      Rs. {config.transportTotal.toLocaleString()}/- Per Player: ₹{transportPerPlayer.toLocaleString()}
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};